export interface ArticleApprovalRequest {
  id: number
  site_id: number
  article_version_id: number
  status: string
  scheduled_upload_at: string | null
  requested_by: number
  requested_at: string
  approved_by?: number | null
  approval_comment: string | null
  approved_at: string | null
  rejected_by?: number | null
  reject_comment: string | null
  rejected_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  uploaded_at: string | null
  upload_error: string | null
}

export interface VoteRequest {
  vote: string
  comment?: string | null
}

export interface ScheduleUpdate {
  scheduled_upload_at: string
}

// -----------------------------------------------
// 記事管理（article_proposals / articles / article_parts）
// -----------------------------------------------

export const ArticleStatus = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending_approval',
  UPLOADED: 'uploaded',
  APPROVED: 'approved',
  PRIVATE: 'private'
} as const

export type ArticleStatus = (typeof ArticleStatus)[keyof typeof ArticleStatus]

export const ArticleCreationSource = {
  NEW: 'new',
  EDIT: 'edit',
  MASTER_SYNC: 'master_sync'
} as const

export type ArticleCreationSource =
  (typeof ArticleCreationSource)[keyof typeof ArticleCreationSource]

// ペルソナ（想定される読み手）の入力上限（backend/schemas/article.py の PERSONA_MAX_LENGTH と対応）
export const ARTICLE_PERSONA_MAX_LENGTH = 1000

export const ArticleTone = {
  PROFESSIONAL: 'professional',
  CASUAL: 'casual',
  FRIENDLY: 'friendly'
} as const

export type ArticleTone = (typeof ArticleTone)[keyof typeof ArticleTone]

export const ArticlePartType = {
  PAGE_TITLE: 'page_title',
  HEADER_BANNER_IMAGE: 'header_banner_image',
  CONTENT_AREA_TOP: 'content_area_top',
  PRODUCT_LIST: 'product_list',
  CONTENT_AREA_BOTTOM: 'content_area_bottom'
} as const

export type ArticlePartType = (typeof ArticlePartType)[keyof typeof ArticlePartType]

// articles の1バージョン
export interface ArticleRecord {
  id: number
  article_proposal_id: number
  version_number: number
  title: string | null
  slug: string | null
  keyword: string | null
  meta_description: string | null
  status: ArticleStatus
  creation_source: ArticleCreationSource
  // このバージョンの作成/編集で AI（自動記事作成・AIリライト）が使われたか
  ai_used: boolean
  // このバージョンの作成起点となった推奨アクション（media_actions）ID。無ければ null
  source_media_action_id: number | null
  heading_outline: string | null
  tone: ArticleTone | null
  // ペルソナ（想定される読み手）
  persona: string | null
  target_char_count: number | null
  source_diagnosis_id: number | null
  site_operator_id: number | null
  site_operator_name: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ArticleApprovalSummary {
  request_id: number
  status: string
  requested_by_name: string | null
  scheduled_upload_at: string | null
  uploaded_at: string | null
  approved_by_name: string | null
  approval_comment: string | null
  approved_at: string | null
  rejected_by_name: string | null
  reject_comment: string | null
  rejected_at: string | null
}

export interface ArticleVersionListItem extends ArticleRecord {
  upload: ArticleApprovalSummary | null
}

// 記事一覧の1行（記事案単位）
export interface ArticleProposalListItem {
  article_proposal_id: number
  title: string | null
  slug: string | null
  status: ArticleStatus
  creation_source: ArticleCreationSource
  updated_at: string | null
  versions: ArticleVersionListItem[]
}

export interface ArticlePartRecord {
  id: number
  part_type: ArticlePartType
  sort_number: number
  content_html: string | null
  image_path: string | null
  page_title: string | null
}

// 特定バージョンの詳細（article_parts含む。差分確認モーダルでの版間比較に使用）
export interface ArticleVersionDetail extends ArticleRecord {
  parts: ArticlePartRecord[]
}

export interface FeaturePageSummary {
  feature_page_id: number | null
  management_title: string | null
  file_name: string | null
  category_id: string | null
}

export interface ArticleProposalDetail {
  article_proposal_id: number
  feature_page: FeaturePageSummary
  latest_article: ArticleRecord
  latest_upload: ArticleApprovalSummary | null
  parts: ArticlePartRecord[]
  history: ArticleVersionListItem[]
}
